import { useState } from 'react';
import ExtraTaskSubmit from './ExtraTaskSubmit';

/**
 * Scrollable list of between-station extra tasks with inline submission.
 *
 * @param {Object} props
 * @param {Array} props.extraTasks - Array of extra task objects from rebus data
 * @param {string} props.teamId - Current team ID
 * @param {string} props.sessionId - Current session ID
 * @param {Object} props.extraTaskResults - Existing submissions { [taskId]: result }
 */
export default function ExtraTaskList({ extraTasks, teamId, sessionId, extraTaskResults }) {
  const [expandedTaskId, setExpandedTaskId] = useState(null);

  const tasks = extraTasks || [];
  const results = extraTaskResults || {};

  if (tasks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-500">
        <span className="material-symbols-outlined text-5xl mb-3">assignment</span>
        <p className="text-lg font-semibold">Ingen ekstraoppgåver</p>
        <p className="text-sm">Denne Mikkelchasen har ikkje ekstraoppgåver</p>
      </div>
    );
  }

  const getTaskLetter = (index) => String.fromCharCode(65 + index); // A, B, C...

  const getStatusInfo = (taskId) => {
    const result = results[taskId];
    if (!result) return { status: 'open', label: null, color: null };
    if (result.approved === true) return { status: 'approved', label: 'Godkjent', color: 'emerald' };
    if (result.approved === false) return { status: 'rejected', label: 'Avvist — prøv igjen', color: 'red' };
    return { status: 'pending', label: 'Ventar', color: 'amber' };
  };

  const toggleExpand = (taskId) => {
    setExpandedTaskId((prev) => (prev === taskId ? null : taskId));
  };

  return (
    <div className="space-y-3 pb-28">
      <div className="flex items-center gap-2 mb-2">
        <span className="material-symbols-outlined text-teal-600">assignment</span>
        <h2 className="font-extrabold text-lg text-gray-900">Ekstraoppgåver</h2>
        <span className="text-sm text-gray-500 ml-auto">
          {Object.keys(results).length}/{tasks.length} levert
        </span>
      </div>

      {tasks.map((task, index) => {
        const { status, label, color } = getStatusInfo(task.id);
        const isExpanded = expandedTaskId === task.id;
        const isSubmitted = status !== 'open';

        return (
          <div
            key={task.id}
            className={`bg-white rounded-2xl shadow-ambient overflow-hidden transition-all ${
              isSubmitted ? 'opacity-80' : ''
            }`}
          >
            {/* Task summary — tappable */}
            <button
              type="button"
              onClick={() => toggleExpand(task.id)}
              className="w-full px-4 py-4 flex items-start gap-3 text-left active:bg-gray-50 transition-colors"
            >
              {/* Letter badge */}
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-extrabold text-lg shrink-0 ${
                isSubmitted ? 'bg-gray-100 text-gray-400' : 'bg-teal-100 text-teal-700'
              }`}>
                {getTaskLetter(index)}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className={`font-semibold text-[15px] leading-snug ${
                  isSubmitted ? 'text-gray-500' : 'text-gray-900'
                }`}>
                  {task.name || task.text}
                </p>
                {task.name && task.text && (
                  <p className={`text-xs leading-snug mt-0.5 ${
                    isSubmitted ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {task.text}
                  </p>
                )}
                <div className="flex items-center gap-2 mt-1.5">
                  {/* Type icon */}
                  <span className="text-sm">
                    {task.type === 'photo' ? '📷' : '✏️'}
                  </span>
                  {/* Points */}
                  <span className="text-xs font-semibold text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full">
                    {task.points || 2} poeng
                  </span>
                  {/* Status badge */}
                  {label && (
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      color === 'emerald' ? 'bg-emerald-100 text-emerald-700' :
                      color === 'red' ? 'bg-red-100 text-red-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {status === 'approved' && '✅ '}
                      {status === 'pending' && '⏳ '}
                      {label}
                    </span>
                  )}
                </div>
              </div>

              {/* Expand chevron */}
              <span className={`material-symbols-outlined text-xl text-gray-400 shrink-0 transition-transform ${
                isExpanded ? 'rotate-180' : ''
              }`}>
                expand_more
              </span>
            </button>

            {/* Expanded submission form */}
            {isExpanded && (
              <div className="px-4 pb-4 pt-3">
                <ExtraTaskSubmit
                  task={task}
                  teamId={teamId}
                  sessionId={sessionId}
                  existingResult={results[task.id]}
                  onSubmit={() => setExpandedTaskId(null)}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
